import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, FileWarning, UserCheck, CreditCard, Tag, Loader2, Search } from "lucide-react";

const money = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n || 0);

export default function ConformiteTarifsModule() {
  const [tab, setTab] = useState("conformite");
  const [conf, setConf] = useState(null);
  const [tar, setTar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [onlyIssues, setOnlyIssues] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/accueil-pilotage/conformite"),
        api.get("/accueil-pilotage/tarifs-audit"),
      ]);
      setConf(a.data); setTar(b.data);
    } catch { toast.error("Chargement échoué."); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !conf || !tar) return (
    <div className="min-h-[60vh] flex items-center justify-center text-zinc-400"><Loader2 className="animate-spin mr-2" /> Analyse en cours…</div>
  );

  const cRows = conf.rows.filter((r) => {
    if (onlyIssues && r.conforme) return false;
    if (q && !(`${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const tRows = tar.rows.filter((r) => !q || `${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase()));

  const Chip = ({ ok, label }) => (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${ok ? "border-emerald-600/40 bg-emerald-900/20 text-emerald-300" : "border-red-600/40 bg-red-900/20 text-red-300"}`}>{ok ? "✓" : "✗"} {label}</span>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="conformite-tarifs-page">
      <div>
        <div className="text-[#C9A961] text-xs uppercase tracking-widest">Accueil & Pilotage · Rentrée</div>
        <h1 className="text-3xl font-bold text-white">Conformité & Tarifs</h1>
        <p className="text-zinc-400 text-sm mt-1">Dossiers complets, activations, paiements — et détection des mauvais forfaits/tarifs.</p>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => setTab("conformite")} data-testid="tab-conformite"
          className={`px-4 py-2 rounded-xl text-sm font-semibold ${tab === "conformite" ? "bg-[#C9A961] text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
          <ShieldCheck className="w-4 h-4 inline mr-1" /> Conformité des membres
        </button>
        <button onClick={() => setTab("tarifs")} data-testid="tab-tarifs"
          className={`px-4 py-2 rounded-xl text-sm font-semibold ${tab === "tarifs" ? "bg-[#C9A961] text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
          <Tag className="w-4 h-4 inline mr-1" /> Tarifs & forfaits {tar.total > 0 && <span className="ml-1 text-[10px] bg-red-600 text-white rounded-full px-1.5">{tar.total}</span>}
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="conf-search" placeholder="Rechercher un membre…"
          className="w-full bg-zinc-900 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white" />
      </div>

      {tab === "conformite" ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="conf-summary">
            <Kpi icon={ShieldCheck} tone="text-emerald-400" v={conf.summary.conformes} l="Conformes" />
            <Kpi icon={AlertTriangle} tone="text-red-400" v={conf.summary.a_corriger} l="À corriger" />
            <Kpi icon={FileWarning} tone="text-amber-400" v={conf.summary.docs} l="Documents manquants" />
            <Kpi icon={UserCheck} tone="text-sky-400" v={conf.summary.activation} l="Activations manquantes" />
            <Kpi icon={CreditCard} tone="text-orange-400" v={conf.summary.paiement} l="Paiements à régler" />
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-400">
            <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} data-testid="conf-only-issues" />
            Afficher uniquement les dossiers à corriger
          </label>
          <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="conf-table">
            {cRows.length === 0 ? <p className="text-zinc-500 text-sm p-6 text-center">Aucun dossier à corriger 🎉</p> : cRows.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-white/5 bg-white/[0.02]" data-testid={`conf-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white font-medium">{r.name} {r.conforme && <span className="text-emerald-400 text-xs">· conforme</span>}</div>
                  <div className="text-[11px] text-zinc-500">{r.email || "aucun courriel"} · {r.discipline} {r.niveau && `· ${r.niveau}`}</div>
                  {r.actions.length > 0 && <div className="text-[11px] text-amber-300 mt-1">À faire : {r.actions.join(" · ")}</div>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Chip ok={r.checks.documents} label="Docs" />
                  <Chip ok={r.checks.activation} label="Activation" />
                  <Chip ok={r.checks.paiement} label="Paiement" />
                  <Chip ok={r.checks.tarif} label="Tarif" />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="tar-summary">
            <Kpi icon={AlertTriangle} tone="text-red-400" v={tar.summary.total_incoherences} l="Incohérences" />
            <Kpi icon={Tag} tone="text-amber-400" v={tar.summary.tarif_manquant} l="Tarif non confirmé" />
            <Kpi icon={CreditCard} tone="text-orange-400" v={tar.summary.montant_incoherent} l="Montant ≠ grille" />
            <Kpi icon={CreditCard} tone="text-red-400" v={tar.summary.en_retard} l="Paiement en retard" />
            <Kpi icon={FileWarning} tone="text-sky-400" v={tar.summary.discipline_manquante} l="Discipline manquante" />
          </div>
          <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="tar-table">
            {tRows.length === 0 ? <p className="text-zinc-500 text-sm p-6 text-center">Aucune incohérence détectée 🎉</p> : tRows.map((r) => (
              <div key={r.id} className="px-4 py-3 border-b border-white/5 bg-white/[0.02]" data-testid={`tar-row-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm text-white font-medium">{r.name} <span className="text-[11px] text-zinc-500">· {r.discipline || "?"} · {r.forfait} · {money(r.tarif_mensuel)}</span></div>
                  <span className="text-[10px] bg-red-600/30 text-red-200 rounded-full px-2 py-0.5">{r.issues.length} problème(s)</span>
                </div>
                <ul className="text-[11px] text-amber-300 mt-1 list-disc list-inside">
                  {r.issues.map((it, idx) => <li key={idx}>{it}</li>)}
                </ul>
                {r.suggestion && (
                  <div className="mt-1.5 text-[11px] text-emerald-300 bg-emerald-950/20 border border-emerald-800/40 rounded-lg px-2 py-1">
                    💡 Correction : {r.suggestion.action}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, tone, v, l }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <Icon className={`w-5 h-5 ${tone} mb-2`} />
      <div className="text-2xl font-extrabold text-white">{v}</div>
      <div className="text-[11px] text-zinc-400 leading-tight">{l}</div>
    </div>
  );
}
